import { useEffect, useMemo, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  createOrgNode,
  updateOrgNode,
  deleteOrgNode,
  orgKeys,
  type OrgNode,
  type OrgNodeUser,
} from "./org.api";
import { SkeletonTableRows } from "@/components/feedback/Skeleton";

interface OrgClientProps {
  nodes: OrgNode[];
  users: OrgNodeUser[];
  isFetching?: boolean;
  isLoading?: boolean;
}

const EXTERNAL_REF_KEYS = [
  "externalReference1",
  "externalReference2",
  "externalReference3",
  "externalReference4",
  "externalReference5",
] as const;
type ExternalRefKey = (typeof EXTERNAL_REF_KEYS)[number];

// ── Tree helpers ──────────────────────────────────────────────────────────────

interface FlatRow {
  node: OrgNode;
  depth: number;
}

// Flatten the adjacency list into a depth-annotated, pre-order list for display.
// Orphans (parent missing) and cycle survivors are surfaced at the root so a
// node can never silently disappear.
function flattenTree(nodes: OrgNode[]): FlatRow[] {
  const ids = new Set(nodes.map((n) => n.id));
  const byParent = new Map<string | null, OrgNode[]>();
  for (const n of nodes) {
    const key = n.parentId && ids.has(n.parentId) ? n.parentId : null;
    const arr = byParent.get(key);
    if (arr) arr.push(n);
    else byParent.set(key, [n]);
  }
  const out: FlatRow[] = [];
  const visited = new Set<string>();
  const sortByName = (a: OrgNode, b: OrgNode) => a.name.localeCompare(b.name);
  const walk = (list: OrgNode[], depth: number) => {
    for (const n of [...list].sort(sortByName)) {
      if (visited.has(n.id)) continue; // cycle guard
      visited.add(n.id);
      out.push({ node: n, depth });
      walk(byParent.get(n.id) ?? [], depth + 1);
    }
  };
  walk(byParent.get(null) ?? [], 0);
  for (const n of nodes) if (!visited.has(n.id)) out.push({ node: n, depth: 0 });
  return out;
}

// Ids of `nodeId` and everything beneath it — the set that is NOT eligible as a
// new parent (would create a loop).
function selfAndDescendantIds(nodeId: string, nodes: OrgNode[]): Set<string> {
  const byParent = new Map<string, string[]>();
  for (const n of nodes) {
    if (!n.parentId) continue;
    const arr = byParent.get(n.parentId);
    if (arr) arr.push(n.id);
    else byParent.set(n.parentId, [n.id]);
  }
  const out = new Set<string>([nodeId]);
  const stack = [...(byParent.get(nodeId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const c of byParent.get(id) ?? []) stack.push(c);
  }
  return out;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ── Drawer ────────────────────────────────────────────────────────────────────

interface DrawerProps {
  item: OrgNode | null; // null = create mode
  nodes: OrgNode[];
  users: OrgNodeUser[];
  onClose: () => void;
  onSaved: () => void;
}

function OrgNodeDrawer({ item, nodes, users, onClose, onSaved }: DrawerProps) {
  const { t } = useTranslation();
  const isCreate = item === null;
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(item?.name ?? "");
  const [parentId, setParentId] = useState<string>(item?.parentId ?? "");
  const [userId, setUserId] = useState<string>(item?.userId ?? "");
  const [refs, setRefs] = useState<Record<ExternalRefKey, string>>(() => ({
    externalReference1: item?.externalReference1 ?? "",
    externalReference2: item?.externalReference2 ?? "",
    externalReference3: item?.externalReference3 ?? "",
    externalReference4: item?.externalReference4 ?? "",
    externalReference5: item?.externalReference5 ?? "",
  }));
  const [showRefs, setShowRefs] = useState(
    EXTERNAL_REF_KEYS.some((k) => (item?.[k] ?? "") !== ""),
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Eligible parents: every node except self + its subtree.
  const blocked = useMemo(
    () => (item ? selfAndDescendantIds(item.id, nodes) : new Set<string>()),
    [item, nodes],
  );
  const parentOptions = useMemo(
    () => nodes.filter((n) => !blocked.has(n.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [nodes, blocked],
  );

  // Users not yet linked to another node (plus the one this node already holds).
  const takenUserIds = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes) if (n.userId && n.id !== item?.id) s.add(n.userId);
    return s;
  }, [nodes, item]);
  const userOptions = useMemo(
    () => users.filter((u) => !takenUserIds.has(u.id)).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users, takenUserIds],
  );

  function buildPayload() {
    return {
      name: name.trim(),
      parentId: parentId || null,
      userId: userId || null,
      externalReference1: refs.externalReference1.trim() || null,
      externalReference2: refs.externalReference2.trim() || null,
      externalReference3: refs.externalReference3.trim() || null,
      externalReference4: refs.externalReference4.trim() || null,
      externalReference5: refs.externalReference5.trim() || null,
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    startTransition(async () => {
      try {
        const payload = buildPayload();
        if (isCreate) await createOrgNode(payload);
        else await updateOrgNode(item.id, payload);
        onSaved();
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  function handleDelete() {
    if (!item) return;
    if (!window.confirm(t("org.deleteConfirm"))) return;
    setError(null);
    startTransition(async () => {
      try {
        await deleteOrgNode(item.id);
        onSaved();
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  return (
    <div
      className="wl-drawer is-open"
      role="dialog"
      aria-label={isCreate ? t("org.newTitle") : item.name}
    >
      <div className="wl-drawer__header">
        <div className="wl-drawer__title" style={{ flex: 1 }}>
          {isCreate ? t("org.newTitle") : item.name}
        </div>
        <button
          className="wl-btn wl-btn--ghost wl-btn--icon"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="wl-drawer__body">
        {error && (
          <div className="wl-alert wl-alert--danger" style={{ marginBottom: "16px" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="wl-drawer__section">
            <div className="wl-drawer__section-title">{t("common.details")}</div>

            <div className="wl-vstack" style={{ gap: "14px" }}>
              <div className="wl-field">
                <label className="wl-field__label" htmlFor="org-name">{t("org.nameLabel")}</label>
                <input
                  id="org-name"
                  className="wl-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("org.namePlaceholder")}
                  required
                  minLength={2}
                />
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="org-parent">{t("org.parentLabel")}</label>
                <select
                  id="org-parent"
                  className="wl-select"
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                >
                  <option value="">{t("org.parentNone")}</option>
                  {parentOptions.map((n) => (
                    <option key={n.id} value={n.id}>{n.name}</option>
                  ))}
                </select>
                <span className="wl-field__help">{t("org.parentHelp")}</span>
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="org-user">{t("org.userLabel")}</label>
                <select
                  id="org-user"
                  className="wl-select"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                >
                  <option value="">{t("org.userNone")}</option>
                  {userOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName} · {u.email}
                    </option>
                  ))}
                </select>
                <span className="wl-field__help">{t("org.userHelp")}</span>
              </div>
            </div>
          </div>

          <div className="wl-divider" style={{ marginBottom: "16px" }} />

          <div className="wl-drawer__section">
            <button
              type="button"
              className="wl-btn wl-btn--ghost wl-btn--sm"
              onClick={() => setShowRefs((v) => !v)}
              style={{ marginBottom: showRefs ? "12px" : 0 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: showRefs ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
              {t("org.externalRefsTitle")}
            </button>
            {showRefs && (
              <>
                <p className="wl-field__help" style={{ marginBottom: "12px" }}>{t("org.externalRefsHelp")}</p>
                <div className="wl-vstack" style={{ gap: "10px" }}>
                  {EXTERNAL_REF_KEYS.map((key, i) => (
                    <div className="wl-field" key={key}>
                      <label className="wl-field__label" htmlFor={`org-${key}`}>
                        {t("org.externalRefLabel", { n: i + 1 })}
                      </label>
                      <input
                        id={`org-${key}`}
                        className="wl-input"
                        type="text"
                        value={refs[key]}
                        onChange={(e) => setRefs((prev) => ({ ...prev, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {!isCreate && (
            <>
              <div className="wl-divider" style={{ marginBottom: "20px" }} />
              <div className="wl-drawer__section">
                <div className="wl-drawer__section-title">{t("common.record")}</div>
                <div className="wl-vstack" style={{ gap: "8px" }}>
                  <div>
                    <div className="t-caption" style={{ marginBottom: "2px" }}>{t("common.id")}</div>
                    <div className="t-mono" style={{ fontSize: "11px", color: "var(--text-secondary)" }}>{item.id}</div>
                  </div>
                  <div>
                    <div className="t-caption" style={{ marginBottom: "2px" }}>{t("common.created")}</div>
                    <div className="t-body-sm">{formatDate(item.createdAt)}</div>
                  </div>
                  <div>
                    <div className="t-caption" style={{ marginBottom: "2px" }}>{t("common.lastUpdated")}</div>
                    <div className="t-body-sm">{formatDate(item.updatedAt)}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div style={{ marginTop: "24px", display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
            {!isCreate && (
              <button
                type="button"
                className="wl-btn wl-btn--danger wl-btn--sm"
                onClick={handleDelete}
                disabled={isPending}
                style={{ marginRight: "auto" }}
              >
                {t("org.deleteNode")}
              </button>
            )}
            <button type="button" className="wl-btn wl-btn--secondary" onClick={onClose} disabled={isPending}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="wl-btn wl-btn--accent" disabled={isPending}>
              {isCreate ? t("common.create") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function OrgClient({ nodes, users, isFetching, isLoading }: OrgClientProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drawerState, setDrawerState] = useState<
    { mode: "create" } | { mode: "edit"; item: OrgNode } | null
  >(null);

  const rows = useMemo(() => flattenTree(nodes), [nodes]);

  function handleSaved() {
    setDrawerState(null);
    queryClient.invalidateQueries({ queryKey: orgKeys.list });
  }

  return (
    <div style={{ position: "relative" }}>
      {drawerState && (
        <>
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(14,20,42,0.3)", zIndex: "var(--z-overlay)" }}
            onClick={() => setDrawerState(null)}
            aria-hidden="true"
          />
          <OrgNodeDrawer
            item={drawerState.mode === "create" ? null : drawerState.item}
            nodes={nodes}
            users={users}
            onClose={() => setDrawerState(null)}
            onSaved={handleSaved}
          />
        </>
      )}

      {/* Toolbar */}
      <div className="wl-hstack" style={{ marginBottom: "16px", gap: "12px" }}>
        <span className="t-caption t-muted">
          {t("org.count", { count: nodes.length })}
        </span>
        <span className="wl-spacer" />
        {isFetching && <span className="t-caption t-muted">{t("common.loading")}</span>}
        <button className="wl-btn wl-btn--accent" onClick={() => setDrawerState({ mode: "create" })}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("org.newNode")}
        </button>
      </div>

      {/* Tree table */}
      <div className="wl-table-wrap">
        <table className="wl-table">
          <thead>
            <tr>
              <th>{t("org.colName")}</th>
              <th>{t("org.colUser")}</th>
              <th style={{ textAlign: "right" }}>{t("org.colChildren")}</th>
            </tr>
          </thead>
          <tbody aria-busy={isLoading || undefined}>
            {isLoading && nodes.length === 0 && <SkeletonTableRows columns={3} />}
            {!isLoading && nodes.length === 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "32px" }}>
                  {t("org.noNodes")}
                </td>
              </tr>
            )}
            {rows.map(({ node, depth }) => (
              <tr
                key={node.id}
                onClick={() => setDrawerState({ mode: "edit", item: node })}
                style={{ cursor: "pointer" }}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setDrawerState({ mode: "edit", item: node }); }}
              >
                <td>
                  <span style={{ paddingLeft: `${depth * 22}px`, display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    {depth > 0 && (
                      <span aria-hidden="true" style={{ color: "var(--text-tertiary)" }}>└</span>
                    )}
                    <span style={{ fontWeight: "var(--fw-medium)" }}>{node.name}</span>
                  </span>
                </td>
                <td>
                  {node.user ? (
                    <span className="wl-chip wl-chip--brand" title={node.user.email}>
                      {node.user.displayName}
                      {!node.user.isActive && (
                        <span className="t-muted" style={{ marginLeft: "4px" }}>· {t("org.userInactive")}</span>
                      )}
                    </span>
                  ) : (
                    <span className="t-caption t-muted">—</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {node.childCount > 0
                    ? <span className="t-body-sm">{node.childCount}</span>
                    : <span className="t-caption t-muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
